import RadarIcon from '@mui/icons-material/Radar';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import LinearProgress from '@mui/material/LinearProgress';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api } from '../../api/client';
import { apiErrorText } from '../../api/errorText';
import type {
  LiveTvProviderSummary,
  LiveTvScanMode,
  LiveTvScanner,
  LiveTvScanStatus,
  LiveTvScanTarget,
} from '../../api/types';
import { useApi } from '../../api/useApi';
import AsyncSection from '../AsyncSection';
import { useProviderName } from './lineup';

/// How often a running scan is asked how far it has got.
const POLL_INTERVAL = 1000;

interface Starting {
  scanner: LiveTvScanner;
  target: LiveTvScanTarget;
}

/// Channel scans: what each scanning plugin can scan, starting one, watching it run, and what
/// it found. A scan takes minutes and runs on the server; this page only asks how it is going,
/// so closing it does not stop anything.
/// It sits on the receiving sources page and shows every scanning plugin there, which is what a
/// server with several tuner plugins needs in one place.
export default function ChannelScanPanel({ providers }: { providers: LiveTvProviderSummary[] | undefined }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const providerName = useProviderName(providers);

  const scanners = useApi(['livetv', 'scanners'], api.liveTvScanners);

  // Polled every second while anything runs, and not at all otherwise.
  const scans = useApi(['livetv', 'scans'], api.liveTvScans, {
    refetchInterval: (data) => (data?.some((scan) => scan.state === 'Running') ? POLL_INTERVAL : false),
  });

  const [starting, setStarting] = useState<Starting>();
  const [error, setError] = useState<string>();

  // When a scan ends, the channel list it changed and the counts shown here are out of date.
  const running = useRef(new Set<string>());

  useEffect(() => {
    const now = new Set((scans.data ?? []).filter((scan) => scan.state === 'Running').map((scan) => scan.providerId));
    const ended = [...running.current].some((providerId) => !now.has(providerId));

    running.current = now;

    if (ended) {
      void queryClient.invalidateQueries({ queryKey: ['livetv', 'channels'] });
      void queryClient.invalidateQueries({ queryKey: ['livetv', 'lineup'] });
      void queryClient.invalidateQueries({ queryKey: ['livetv', 'bouquet'] });
      void queryClient.invalidateQueries({ queryKey: ['livetv', 'scanners'] });
    }
  }, [scans.data, queryClient]);

  const statusOf = (scanner: LiveTvScanner): LiveTvScanStatus | null =>
    scans.data?.find((scan) => scan.providerId === scanner.providerId) ?? scanner.scan;

  const cancel = async (providerId: string) => {
    setError(undefined);

    try {
      await api.cancelLiveTvScan(providerId);
    } catch (caught) {
      const text = apiErrorText(caught, t);
      setError([text.message, text.remedy].filter(Boolean).join(' '));
    } finally {
      scans.reload();
    }
  };

  return (
    <Stack spacing={2}>
      <Typography color="text.secondary" sx={{ maxWidth: '70ch' }}>
        {t('livetvChannels.scan.intro')}
      </Typography>

      {error && (
        <Alert severity="error" onClose={() => setError(undefined)}>
          {error}
        </Alert>
      )}

      <AsyncSection loading={scanners.loading} error={scanners.error} onRetry={scanners.reload}>
        {(scanners.data ?? []).length === 0 ? (
          <Alert severity="info">{t('livetvChannels.scan.none')}</Alert>
        ) : (
          <Stack spacing={2}>
            {scanners.data?.map((scanner) => {
              const status = statusOf(scanner);
              const busy = status?.state === 'Running';

              return (
                <Card key={scanner.providerId} variant="outlined">
                  <CardContent>
                    <Stack spacing={2}>
                      <Typography variant="h3" sx={{ fontSize: 18 }}>
                        {providerName(scanner.providerId, scanner.displayNameKey)}
                      </Typography>

                      {scanner.error && <Alert severity="warning">{scanner.error}</Alert>}

                      {scanner.targets.length === 0 && !scanner.error && (
                        <Typography color="text.secondary">{t('livetvChannels.scan.noTargets')}</Typography>
                      )}

                      <Stack divider={<Divider flexItem />} spacing={1.5}>
                        {scanner.targets.map((target) => (
                          <TargetRow
                            key={target.id}
                            target={target}
                            last={scanner.lastScans.find((scan) => scan.targetId === target.id)}
                            disabled={busy}
                            onScan={() => {
                              setError(undefined);
                              setStarting({ scanner, target });
                            }}
                          />
                        ))}
                      </Stack>

                      {status && <ScanStatus status={status} onCancel={() => void cancel(scanner.providerId)} />}
                    </Stack>
                  </CardContent>
                </Card>
              );
            })}
          </Stack>
        )}
      </AsyncSection>

      <Box>
        <Button onClick={scanners.reload}>{t('livetvChannels.scan.reload')}</Button>
      </Box>

      {starting && (
        <StartScanDialog
          starting={starting}
          onClose={() => setStarting(undefined)}
          onStarted={() => {
            setStarting(undefined);
            scans.reload();
          }}
        />
      )}
    </Stack>
  );
}

/// One thing that can be scanned: which device and input, how many channels came from it
/// before, and when it was last scanned to the end.
function TargetRow({
  target,
  last,
  disabled,
  onScan,
}: {
  target: LiveTvScanTarget;
  last: LiveTvScanStatus | undefined;
  disabled: boolean;
  onScan: () => void;
}) {
  const { t, i18n } = useTranslation();
  const lastScanned = target.lastScannedAt ?? (last?.state === 'Completed' ? last.endedAt : null);

  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={1.5}
      sx={{ alignItems: { xs: 'stretch', sm: 'center' } }}
    >
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        {/* The device's own name - hardware is not translated. */}
        <Typography sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>{target.name}</Typography>

        <Stack direction="row" useFlexGap spacing={1} sx={{ flexWrap: 'wrap', mt: 0.5, alignItems: 'center' }}>
          <Chip size="small" label={t(`livetvChannels.delivery.${target.deliverySystem}`)} />
          <Typography variant="caption" color="text.secondary">
            {target.knownChannels > 0
              ? t('livetvChannels.scan.known', { total: target.knownChannels })
              : t('livetvChannels.scan.firstScan')}
          </Typography>
          {lastScanned && (
            <Typography variant="caption" color="text.secondary">
              {t('livetvChannels.scan.lastScanned', { date: new Date(lastScanned).toLocaleString(i18n.language) })}
            </Typography>
          )}
        </Stack>

        {target.error && (
          <Typography variant="body2" color="warning.main" sx={{ mt: 0.5 }}>
            {target.error}
          </Typography>
        )}
      </Box>

      <Button
        variant="contained"
        startIcon={<RadarIcon />}
        disabled={disabled || target.error !== null}
        onClick={onScan}
        sx={{ flexShrink: 0 }}
      >
        {t('livetvChannels.scan.start')}
      </Button>
    </Stack>
  );
}

/// How the provider's scan stands: running, with what it has found so far, or how it ended.
function ScanStatus({ status, onCancel }: { status: LiveTvScanStatus; onCancel: () => void }) {
  const { t, i18n } = useTranslation();

  if (status.state === 'Running') {
    const progress = status.progress;
    const percent = Math.round(Math.min(Math.max(progress?.fraction ?? 0, 0), 1) * 100);

    return (
      <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 2 }} aria-live="polite">
        <Stack spacing={1.5}>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }} useFlexGap>
            <Typography sx={{ fontWeight: 600, flexGrow: 1 }}>
              {t('livetvChannels.scan.running', { target: status.targetName })}
            </Typography>
            <Button color="warning" variant="outlined" onClick={onCancel}>
              {t('livetvChannels.scan.cancel')}
            </Button>
          </Stack>

          <LinearProgress
            variant={progress ? 'determinate' : 'indeterminate'}
            value={percent}
            aria-label={t('livetvChannels.scan.progress', { percent })}
          />

          <Typography variant="body2" color="text.secondary">
            {progress
              ? t('livetvChannels.scan.progress', { percent })
              : t('livetvChannels.scan.waiting')}
            {progress && progress.transponderCount > 0 && (
              <>
                {' · '}
                {t('livetvChannels.scan.transponders', {
                  done: progress.transpondersDone,
                  total: progress.transponderCount,
                })}
              </>
            )}
          </Typography>

          {progress?.current && (
            <Typography variant="body2" sx={{ fontFamily: 'monospace', overflowWrap: 'anywhere' }}>
              {t('livetvChannels.scan.current', { current: progress.current })}
            </Typography>
          )}

          {progress && (
            <Stack direction="row" useFlexGap spacing={1} sx={{ flexWrap: 'wrap' }}>
              <Chip size="small" label={t('livetvChannels.scan.foundTelevision', { total: progress.televisionFound })} />
              <Chip size="small" label={t('livetvChannels.scan.foundRadio', { total: progress.radioFound })} />
              <Chip
                size="small"
                variant="outlined"
                label={t('livetvChannels.scan.foundEncrypted', { total: progress.encryptedFound })}
              />
            </Stack>
          )}
        </Stack>
      </Box>
    );
  }

  const when = status.endedAt ? new Date(status.endedAt).toLocaleString(i18n.language) : '';

  if (status.state === 'Failed') {
    return (
      <Alert severity="error">
        <strong>{t('livetvChannels.scan.failed', { target: status.targetName, date: when })}</strong>
        <div>
          {status.errorCode
            ? t(status.errorCode, { defaultValue: status.error ?? '' })
            : status.error}
        </div>
      </Alert>
    );
  }

  if (status.state === 'Cancelled') {
    return (
      <Alert severity="info">
        {t(status.mode === 'Merge' ? 'livetvChannels.scan.cancelledMerge' : 'livetvChannels.scan.cancelledReplace', {
          target: status.targetName,
        })}
      </Alert>
    );
  }

  const result = status.result;

  return (
    <Alert severity={result && result.failures.length > 0 ? 'warning' : 'success'}>
      <strong>{t('livetvChannels.scan.completed', { target: status.targetName, date: when })}</strong>
      {result && (
        <>
          <div>
            {t('livetvChannels.scan.summary', {
              television: result.televisionFound,
              radio: result.radioFound,
              encrypted: result.encryptedFound,
            })}
          </div>
          {status.mode === 'Replace' && <div>{t('livetvChannels.scan.removed', { total: result.removed })}</div>}
          {result.failures.length > 0 && (
            <Box component="details" sx={{ mt: 1 }}>
              <Box component="summary" sx={{ cursor: 'pointer' }}>
                {t('livetvChannels.scan.failures', { total: result.failures.length })}
              </Box>
              {/* What the tuner said about each transponder, in the tuner's own words. */}
              <Box component="ul" sx={{ m: 0, mt: 0.5, paddingInlineStart: 3 }}>
                {result.failures.map((failure, index) => (
                  <li key={index}>
                    <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                      {failure}
                    </Typography>
                  </li>
                ))}
              </Box>
            </Box>
          )}
        </>
      )}
    </Alert>
  );
}

/// What to do with what the scan finds, asked before it starts.
function StartScanDialog({
  starting,
  onClose,
  onStarted,
}: {
  starting: Starting;
  onClose: () => void;
  onStarted: () => void;
}) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<LiveTvScanMode>('Replace');
  const [includeEncrypted, setIncludeEncrypted] = useState(true);
  const [includeRadio, setIncludeRadio] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  const start = async () => {
    setBusy(true);
    setError(undefined);

    try {
      await api.startLiveTvScan({
        providerId: starting.scanner.providerId,
        targetId: starting.target.id,
        mode,
        includeEncrypted,
        includeRadio,
      });
      onStarted();
    } catch (caught) {
      // Busy, gone, or not scannable right now: the server says which, and what is running.
      const text = apiErrorText(caught, t);
      setError([text.message, text.remedy].filter(Boolean).join(' '));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('livetvChannels.scan.dialogTitle')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>{starting.target.name}</Typography>

          {error && <Alert severity="error">{error}</Alert>}

          <RadioGroup value={mode} onChange={(event) => setMode(event.target.value as LiveTvScanMode)}>
            <FormControlLabel
              value="Replace"
              control={<Radio />}
              label={
                <Box sx={{ py: 0.5 }}>
                  <Typography>{t('livetvChannels.scan.mode.Replace')}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('livetvChannels.scan.mode.ReplaceHint')}
                  </Typography>
                </Box>
              }
            />
            <FormControlLabel
              value="Merge"
              control={<Radio />}
              label={
                <Box sx={{ py: 0.5 }}>
                  <Typography>{t('livetvChannels.scan.mode.Merge')}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('livetvChannels.scan.mode.MergeHint')}
                  </Typography>
                </Box>
              }
            />
          </RadioGroup>

          <FormControlLabel
            control={<Switch checked={includeEncrypted} onChange={(event) => setIncludeEncrypted(event.target.checked)} />}
            label={t('livetvChannels.scan.includeEncrypted')}
          />
          <FormControlLabel
            control={<Switch checked={includeRadio} onChange={(event) => setIncludeRadio(event.target.checked)} />}
            label={t('livetvChannels.scan.includeRadio')}
          />

          <Typography variant="caption" color="text.secondary">
            {t('livetvChannels.scan.tunerHint')}
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button variant="contained" disabled={busy} onClick={() => void start()}>
          {t('livetvChannels.scan.start')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
